import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import { useCallback } from 'react';

import useProductAnalytics from '@/analytics/hooks/useProductAnalytics';
import { EventType } from '@/analytics/types';
import {
  EXTENDED_WARRANTY_2YEAR_ID,
  EXTENDED_WARRANTY_2YEAR_SKU,
  EXTENDED_WARRANTY_3YEAR_ID,
  EXTENDED_WARRANTY_3YEAR_SKU,
  WIZARD_PAGE_VIEWED,
} from '@/app/[locale]/product/_consts';
import logToDatadog from '@/logger/logToDatadog';
import { addToCart } from '@/queries/AddToCart';
import { changeWarranty } from '@/queries/ChangeWarranty';
import { removeFromCart } from '@/queries/RemoveFromCart';
import { getCartIdCookie } from '@/utils/cartUtils';

import sendBundleAnalytics from '../_analytics/sendBundleAnalytics';
import MembershipSelection from '../_steps/enums/MembershipSelection';
import WarrantySelection from '../_steps/enums/WarrantySelection';
import type { StockStatus } from '../_types/inventoryTypes';
import type { UseWizardState } from './useWizardState/types';

export function getExtendedWarrantySku(
  selection: WarrantySelection | undefined,
): string | null {
  switch (selection) {
    case WarrantySelection.TwoYear:
      return EXTENDED_WARRANTY_2YEAR_SKU;
    case WarrantySelection.ThreeYear:
      return EXTENDED_WARRANTY_3YEAR_SKU;
    default:
      return null;
  }
}

export function getExtendedWarrantyId(
  selection: WarrantySelection | undefined,
): number | null {
  switch (selection) {
    case WarrantySelection.TwoYear:
      return EXTENDED_WARRANTY_2YEAR_ID;

    case WarrantySelection.ThreeYear:
      return EXTENDED_WARRANTY_3YEAR_ID;

    case WarrantySelection.OneYear:
    default:
      return null;
  }
}

interface AddProductToCartProps {
  variantId: { sku: string };
  isAccessory: boolean | undefined;
  membershipObj: {
    product_id: number | undefined;
    sku: string;
    membershipSelection: MembershipSelection | undefined;
  };
  onAddToCartSuccess: () => Promise<void>;
  ringCount: number | undefined;
  warrantySku: string;
}

const addProductToCart = async ({
  variantId,
  isAccessory,
  membershipObj,
  onAddToCartSuccess,
  ringCount,
  warrantySku,
}: AddProductToCartProps): Promise<void> => {
  try {
    const onSuccess = () => {
      // When cart 2.0 is enabled, use this as the trigger to advance to the next page.
      void onAddToCartSuccess();
    };

    // Add item to the Cart 2.0 cart.
    await addToCart(variantId.sku, warrantySku);
    if (membershipObj.membershipSelection === MembershipSelection.OneYear) {
      await addToCart(membershipObj.sku, warrantySku);
    } else {
      await removeFromCart(membershipObj.sku, null);
    }

    // TODO: ECOM-5460 move this logic to the backend, and remove from here
    const shouldChangeWarranty = (ringCount ?? 0) > 0 && !isAccessory;
    // update warranties when this is not the first ring
    if (shouldChangeWarranty) {
      try {
        await changeWarranty(warrantySku);
        onSuccess();
      } catch (e) {
        void logToDatadog(
          'cart',
          'cart 2.0 changeWarranty failed after addToCart',
          { cause: e, warrantySku, ringCount },
        );
      }
    } else {
      onSuccess();
    }
  } catch (e) {
    void logToDatadog(
      'cart',
      'Error thrown during addToCart (cart 2.0)',
      { error: e },
      'error',
    );
  }
};

interface UseAddToCartProps {
  stockStatus: StockStatus;
}

const useAddToCart = ({
  currentRoute,
  isAccessory,
  membershipSelection,
  oneYearMembership,
  product,
  ringCount,
  stockStatus,
  warrantySelection,
  setIsAddingToCart,
}: UseWizardState & UseAddToCartProps) => {
  const router = useRouter();
  const locale = useLocale();
  const { sendProductAddedEvent } = useProductAnalytics();

  const onAddToCartSuccess = useCallback(async () => {
    const style = currentRoute.style;
    const finish = currentRoute.finish;
    const size = currentRoute.size;
    const sizeValue = size.value;

    await sendProductAddedEvent({
      path: `product/rings/${style.value}/${finish.value}/${sizeValue}`,
      product,
      stockStatus,
    });

    localStorage.removeItem(WIZARD_PAGE_VIEWED);
    router.push(`/${locale}/cart`);
  }, [locale, router]); // eslint-disable-line react-hooks/exhaustive-deps -- fix in follow-up

  const handleAddToCart = useCallback(async () => {
    setIsAddingToCart(true);

    const variants = product?.variants;
    const productsVariants = oneYearMembership?.variants;
    const size = currentRoute.size;
    const sizeValue = size.value;

    const cartId = getCartIdCookie();

    const membershipSku: string = Array.isArray(productsVariants)
      ? (productsVariants[0]?.sku ?? '')
      : '';

    const productId = getExtendedWarrantyId(warrantySelection);
    const warrantySku = getExtendedWarrantySku(warrantySelection) ?? '';

    const membershipObj = {
      product_id: oneYearMembership?.id,
      sku: membershipSku,
      membershipSelection,
    };

    const warrantyObj = {
      product_id: productId,
      sku: warrantySku,
      warrantySelection,
    };
    if (!isAccessory) {
      void sendBundleAnalytics({
        cart_id: cartId,
        membershipObj,
        warrantyObj,
      });
    } else {
      // Add to cart button clicked for accessories
      await window.ouraAnalytics.track(EventType.CTAClicked, {
        action: 'cart',
        cta: 'add_to_cart',
        location: 'wizard',
        path: `product/accessories/${currentRoute.accessory.value}/${sizeValue}`,
      });
    }

    // this checks the size value and returns either a value or mapped value to get the variant
    const selectOptions = sizeValue === 'sizing-kit' ? 'Size later' : sizeValue;
    const variantId = variants?.find((v) => {
      if (Array.isArray(v.selectedOptions) && v.selectedOptions.length > 0) {
        return (
          v.selectedOptions.find((o) => o?.name === 'Size')?.value ===
          selectOptions
        );
      }
      return false;
    });

    if (!variantId || !membershipObj.sku) return;

    void addProductToCart({
      variantId,
      isAccessory,
      membershipObj,
      onAddToCartSuccess,
      ringCount,
      warrantySku,
    });
  }, [
    setIsAddingToCart,
    product,
    oneYearMembership?.variants,
    oneYearMembership?.id,
    currentRoute,
    warrantySelection,
    membershipSelection,
    isAccessory,
    onAddToCartSuccess,
    ringCount,
  ]);

  return { onAddToCart: handleAddToCart };
};

export default useAddToCart;
